import type { DisplayJob } from '../types';
import type { JobFormState } from '../redux/features/jobFormSlice';
import type { ComparisonWeights } from './jobComparison';

export interface MatchScoreBreakdown {
  overall: number;
  salary: number;
  vacation: number;
  remoteness: number;
  benefits: number;
  languages: number;
  weights: ComparisonWeights;
}

/**
 * Calculate a match score (0-100) for a single job based on user profile and comparison weights.
 * This uses the same logic as jobComparison but adapted for single job scoring.
 * Returns both the overall score and the breakdown.
 */
export function calculateJobMatchScore(
  job: DisplayJob,
  userProfile: JobFormState,
  weights: ComparisonWeights
): MatchScoreBreakdown | null {
  // If user hasn't filled out their profile, return null (not zero)
  // This helps distinguish between "no profile" and "score is 0"
  if (!userProfile.currentJob || (!userProfile.currentJob.salary && !userProfile.currentJob.title && !userProfile.currentJob.normalisedTitle)) {
    return null;
  }

  const currentJob = userProfile.currentJob;
  const w: ComparisonWeights = {
    salaryWeight: weights.salaryWeight ?? 0.2,
    vacationWeight: weights.vacationWeight ?? 0.2,
    benefitsWeight: weights.benefitsWeight ?? 0.2,
    remotenessWeight: weights.remotenessWeight ?? 0.2,
    languagesWeight: (weights as ComparisonWeights & { languagesWeight?: number }).languagesWeight ?? 0.2,
  };
  
  // Convert user profile to DisplayJob format for comparison
  const userJob: DisplayJob = {
    objectID: 'user-current',
    title: currentJob.title || '',
    normalisedTitle: currentJob.normalisedTitle || '',
    normalisedRoleLevel: currentJob.normalisedRoleLevel || '',
    description: currentJob.description || '',
    requirements: [],
    responsibilities: [],
    skillsAndTechnologies: [],
    location: Array.isArray(currentJob.locations) ? currentJob.locations : [],
    remoteness: currentJob.remoteness || '',
    jobType: currentJob.jobType || '',
    // Profile: monthly salary + work %. Convert to yearly 100% FTE for comparison with job listings
    ...(function () {
      const monthly = typeof currentJob.salary === 'number' ? currentJob.salary : 0;
      const pct = typeof (currentJob as { workPercentage?: number }).workPercentage === 'number'
        ? Math.max(1, Math.min(100, (currentJob as { workPercentage: number }).workPercentage))
        : 100;
      const yearlyFte = monthly * 12 * (100 / pct);
      return { salaryRangeMin: yearlyFte, salaryRangeMax: yearlyFte };
    })(),
    salaryDetails: '',
    specialisations: [],
    requiredLanguages: [],
    optionalLanguages: [],
    postedDate: '',
    applicationDeadline: '',
    contactEmail: '',
    companyId: '',
    logoUrl: undefined,
    companyIndustry: undefined,
    employerOverview: undefined,
    companyContactDetails: undefined,
    benefits: Array.isArray(currentJob.benefits) ? currentJob.benefits : [],
    vacationWeeks: typeof currentJob.vacationWeeks === 'number' ? currentJob.vacationWeeks : 0,
    recruitmentProcess: [],
    educationLevel: '',
    minimumYearsOfExperience: 0,
    perks: [],
    url: '',
    processedAt: '',
    firstSeen: '',
    lastSeen: '',
    isActive: true,
    field: '',
    currency: 'EUR',
    score: 0,
  };

  // Use the same scoring logic as jobComparison but for single job comparison
  const allJobs = [userJob, job];
  
  // Calculate individual scores
  const salaryMins = allJobs.map((j) => (typeof j.salaryRangeMin === 'number' ? j.salaryRangeMin : 0));
  const salaryMaxs = allJobs.map((j) => (typeof j.salaryRangeMax === 'number' ? j.salaryRangeMax : 0));
  const maxSalary = Math.max(...salaryMaxs, 0);
  const minSalary = Math.min(...salaryMins, 0);
  const salaryRange = maxSalary - minSalary || 1;
  const jobSalaryMin = typeof job.salaryRangeMin === 'number' ? job.salaryRangeMin : 0;
  let salaryScore = ((jobSalaryMin - minSalary) / salaryRange) * 100;
  if (!isFinite(salaryScore) || isNaN(salaryScore)) salaryScore = 0;
  // If job salary is higher than user's current, give bonus
  if (jobSalaryMin > userJob.salaryRangeMin) {
    salaryScore = Math.min(100, salaryScore + 20);
  }

  // Vacation
  const vacationWeeksArr = allJobs.map((j) => (typeof j.vacationWeeks === 'number' ? j.vacationWeeks : 0));
  const maxVacation = Math.max(...vacationWeeksArr, 0);
  const minVacation = Math.min(...vacationWeeksArr, 0);
  const vacationRange = maxVacation - minVacation || 1;
  const jobVacation = typeof job.vacationWeeks === 'number' ? job.vacationWeeks : 0;
  let vacationScore = ((jobVacation - minVacation) / vacationRange) * 100;
  if (!isFinite(vacationScore) || isNaN(vacationScore)) vacationScore = 0;
  // If job vacation is more than user's current, give bonus
  if (jobVacation >= userJob.vacationWeeks) {
    vacationScore = Math.min(100, vacationScore + 30);
  }

  // Remoteness
  let remotenessScore = 0;
  if (typeof job.remoteness === 'string' && typeof userJob.remoteness === 'string') {
    const jobRemoteness = job.remoteness.toLowerCase();
    const userRemoteness = userJob.remoteness.toLowerCase();
    
    if (jobRemoteness === userRemoteness) {
      remotenessScore = 100;
    } else if (userRemoteness === 'hybrid' || jobRemoteness === 'hybrid') {
      remotenessScore = 60;
    } else if (userRemoteness === 'remote' && jobRemoteness === 'onsite') {
      remotenessScore = 0;
    } else if (userRemoteness === 'onsite' && jobRemoteness === 'remote') {
      remotenessScore = 80; // Remote is generally preferred
    } else {
      remotenessScore = 0;
    }
  }
  if (!isFinite(remotenessScore) || isNaN(remotenessScore)) remotenessScore = 0;

  // Benefits
  const benefitsArr = allJobs.map((j) => (Array.isArray(j.benefits) ? j.benefits.length : 0));
  const maxBenefits = Math.max(...benefitsArr, 0);
  const minBenefits = Math.min(...benefitsArr, 0);
  const benefitsRange = maxBenefits - minBenefits || 1;
  const jobBenefits = Array.isArray(job.benefits) ? job.benefits.length : 0;
  let benefitsScore = ((jobBenefits - minBenefits) / benefitsRange) * 100;
  if (!isFinite(benefitsScore) || isNaN(benefitsScore)) benefitsScore = 0;
  // If job has more benefits than user's current, give bonus
  if (jobBenefits > (Array.isArray(userJob.benefits) ? userJob.benefits.length : 0)) {
    benefitsScore = Math.min(100, benefitsScore + 20);
  }

  // Languages: compare user's professional languages with job required/optional
  const userLangs = (currentJob.languages ?? []).map((l) => String(l).toLowerCase().trim()).filter(Boolean);
  const required = Array.isArray(job.requiredLanguages) ? job.requiredLanguages : [];
  const optional = Array.isArray(job.optionalLanguages) ? job.optionalLanguages : [];
  let languagesScore = 100;
  if (required.length > 0) {
    const requiredMet = required.filter((r) => userLangs.some((u) => u === String(r).toLowerCase().trim())).length;
    languagesScore = (requiredMet / required.length) * 100;
  } else if (optional.length > 0) {
    const optionalMet = optional.filter((o) => userLangs.some((u) => u === String(o).toLowerCase().trim())).length;
    languagesScore = optionalMet / optional.length * 100;
  }
  if (!isFinite(languagesScore) || isNaN(languagesScore)) languagesScore = 100;

  // Calculate weighted overall score
  let overallScore =
    salaryScore * w.salaryWeight +
    vacationScore * w.vacationWeight +
    remotenessScore * w.remotenessWeight +
    benefitsScore * w.benefitsWeight +
    languagesScore * w.languagesWeight;
  if (!isFinite(overallScore) || isNaN(overallScore)) overallScore = 0;

  return {
    overall: Math.max(0, Math.min(100, Math.round(overallScore))),
    salary: Math.round(salaryScore),
    vacation: Math.round(vacationScore),
    remoteness: Math.round(remotenessScore),
    benefits: Math.round(benefitsScore),
    languages: Math.round(languagesScore),
    weights: w,
  };
}
